/**
 * Claims API
 * Knowledge claims review queue operations
 */

import { apiFetch } from './config';

export interface Claim {
  claim_id: string;
  claim_text: string;
  claim_type: string | null;
  domain: string | null;
  tags: string[] | null;
  source_object_id: string | null;
  source_object_type: string | null;
  source_field: string | null;
  extraction_method: string;
  extracted_by: string | null;
  provenance_class: string;
  provenance_weight: number;
  computed_confidence: number | null;
  corroboration_count: number;
  status: 'proposed' | 'active' | 'disputed' | 'retracted' | 'superseded';
  source_project: string | null;
  scope: string | null;
  asserting_authority: string | null;
  adopting_authority: string | null;
  valid_from: string | null;
  valid_until: string | null;
  temporal_class: string | null;
  currency: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  conversation_id: string | null;
  chunk_index: number | null;
  canonical_conflicts: Array<{ conflict_id: string; canonical_value: string; source?: string }> | null;
  created_at: string;
  updated_at: string;
  search_rank?: number;
}

export interface ClaimsListResponse {
  records: Claim[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface ClaimsCounts {
  status_counts: {
    total: number;
    proposed: number;
    active: number;
    disputed: number;
    retracted: number;
    superseded: number;
  };
  domain_counts: Array<{ domain: string; cnt: number }>;
  source_project_counts: Array<{ source_project: string; cnt: number }>;
  provenance_class_counts: Array<{ provenance_class: string; cnt: number }>;
}

export type ClaimVerdict = 'confirm' | 'dispute' | 'retract' | 'defer';

export interface ClaimReviewPayload {
  claim_ids: string[];
  verdict: ClaimVerdict;
  reason_code?: string;
  notes?: string;
  superseded_by?: string;
  currency?: 'current' | 'historical_only' | 'superseded';
}

export interface ClaimReviewResult {
  claim_id: string;
  success: boolean;
  new_status?: string;
  error?: string;
}

export interface ClaimReviewResponse {
  verdict: string;
  reason_code: string | null;
  reviewer: string;
  reviewer_maturity: string;
  succeeded: number;
  failed: number;
  total: number;
  results: ClaimReviewResult[];
}

export interface TranscriptMessage {
  sender: string;
  text: string;
  index: number;
  is_match?: boolean;
}

export interface TranscriptChunkResponse {
  conversation_id: string;
  chunk_index: number | null;
  total_chunks?: number;
  messages: TranscriptMessage[] | null;
  conversation_name: string | null;
  turn_count: number;
  claim_id?: string;
  match_found?: boolean;
  match_message_index?: number;
  note?: string;
}

export interface ConversationGroup {
  conversation_id: string;
  claim_count: number;
  earliest: string;
  latest: string;
  projects: string[];
  claims: Claim[];
}

export interface SubjectGroup {
  subject: string;
  claim_count: number;
  claims: Claim[];
}

export interface ClaimsGroupedResponse {
  groups: (ConversationGroup | SubjectGroup)[];
  ungrouped_count: number;
  total_claims: number;
  pagination: {
    page: number;
    groups_per_page: number;
    total_groups: number;
    total_pages: number;
  };
}

export interface ClaimsListParams {
  status?: string;
  domain?: string;
  source_project?: string;
  provenance_class?: string;
  scope?: string;
  extraction_method?: string;
  q?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  group_by?: 'conversation' | 'subject';
  groups_per_page?: number;
  group_sort?: 'size' | 'date';
}

export const claimsApi = {
  list: async (params?: ClaimsListParams) => {
    const qs = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
      });
    }
    const query = qs.toString();
    return apiFetch<ClaimsListResponse>(
      `/api/claims${query ? '?' + query : ''}`
    );
  },

  listGrouped: async (params?: ClaimsListParams) => {
    const qs = new URLSearchParams();
    qs.set('group_by', 'conversation');
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
      });
    }
    const query = qs.toString();
    return apiFetch<ClaimsGroupedResponse>(
      `/api/claims?${query}`
    );
  },

  getCounts: async () => {
    return apiFetch<ClaimsCounts>('/api/claims?counts_only=true');
  },

  review: async (payload: ClaimReviewPayload) => {
    return apiFetch<ClaimReviewResponse>('/api/claims/review', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getTranscriptChunk: async (conversationId: string, chunkIndex: number | string) => {
    return apiFetch<TranscriptChunkResponse>(
      `/api/transcripts/${conversationId}/chunk/${chunkIndex}`
    );
  },

  getTranscriptContext: async (conversationId: string, claimId: string) => {
    return apiFetch<TranscriptChunkResponse>(
      `/api/transcripts/${conversationId}/chunk/meta?claim_id=${encodeURIComponent(claimId)}`
    );
  },
};
